/*
  Affiliate CTA click tracking -- CP-5.

  WHY THIS EXISTS
    The purchase completes off-site, so we cannot observe it. Until affiliate
    reporting is available, the outbound click IS the conversion metric -- see
    the affiliate migration gate in docs/IMPLEMENTATION_ROADMAP.md. This is the
    only signal we own, which is also why it is instrumented before the feature
    is ever switched on rather than after.

  DESIGN NOTES
    - Every destination is guarded with `typeof ... !== 'undefined'`, matching
      the existing giveaway-button pattern in layout/theme.liquid. No analytics
      library is a dependency; if none is present the click still works.
    - The link opens in a new tab, so this page is never unloaded and the beacon
      has time to send. No sendBeacon or unload hack is needed.
    - Listener is delegated on document, so it survives section re-renders in the
      theme editor and any variant-change re-paint of the buy-buttons block.
    - It never calls preventDefault. Navigation must not depend on analytics.
*/
(function () {
  'use strict';

  function track(link) {
    var title = link.getAttribute('data-product-title') || '';
    var sku = link.getAttribute('data-sku') || '';
    var seller = link.getAttribute('data-seller') || '';
    var currency = link.getAttribute('data-currency') || 'DKK';
    var value = parseFloat(link.getAttribute('data-price') || '0') || 0;
    var productId = link.getAttribute('data-product-id') || '';
    var destination = link.href || '';
    // Contract fields 8 and 9 (P5). Upper-cased because 'dk' and 'DK' must
    // never become two dimensions in the same report.
    var country = (link.getAttribute('data-country') || 'DK').toUpperCase();
    var site = link.getAttribute('data-site') || 'walkingpad32.shop';

    // GA4. `value` and `currency` are included so this can be promoted to a
    // conversion with monetary value without re-instrumenting.
    if (typeof gtag !== 'undefined') {
      gtag('event', 'affiliate_click', {
        event_category: 'Affiliate',
        event_label: title,
        currency: currency,
        value: value,
        affiliate_seller: seller,
        destination: destination,
        country: country,
        site: site,
        items: [{
          item_id: sku || productId,
          item_name: title,
          item_brand: seller,
          price: value,
          quantity: 1
        }]
      });
    }

    // Google Tag Manager, when present instead of or alongside gtag.
    if (typeof window.dataLayer !== 'undefined' && window.dataLayer) {
      window.dataLayer.push({
        event: 'affiliate_click',
        affiliate_seller: seller,
        affiliate_destination: destination,
        product_id: productId,
        product_title: title,
        product_sku: sku,
        value: value,
        currency: currency,
        country: country,
        site: site
      });
    }

    // Shopify's own analytics. MEASURED 2026-07-27: this is the ONLY analytics
    // system actually installed on this storefront -- gtag, dataLayer, ga and
    // fbq are all undefined, and the sole analytics script is trekkie. The
    // destinations above are kept because they cost nothing and an app may add
    // one at any time, but without this branch the affiliate_click event would
    // have had nowhere to go.
    //
    // Consuming it requires a Custom Pixel in the Admin subscribed to
    // 'affiliate_click'. Publishing without a subscriber is harmless.
    if (window.Shopify && window.Shopify.analytics &&
        typeof window.Shopify.analytics.publish === 'function') {
      window.Shopify.analytics.publish('affiliate_click', {
        product_id: productId,
        product_title: title,
        product_sku: sku,
        affiliate_seller: seller,
        affiliate_destination: destination,
        value: value,
        currency: currency,
        country: country,
        site: site
      });
    }

    // Meta. trackCustom rather than a standard event: this is an outbound
    // intent, not a purchase or a checkout we can confirm.
    if (typeof fbq !== 'undefined') {
      fbq('trackCustom', 'AffiliateClick', {
        content_name: title,
        content_ids: [sku || productId],
        value: value,
        currency: currency,
        seller: seller,
        country: country,
        site: site
      });
    }
  }

  document.addEventListener('click', function (event) {
    var link = event.target && event.target.closest
      ? event.target.closest('[data-affiliate-link]')
      : null;
    if (!link) return;
    try {
      track(link);
    } catch (e) {
      // Never let instrumentation break the purchase path.
      if (window.console && console.warn) {
        console.warn('[affiliate-cta] tracking failed', e);
      }
    }
  });

  /*
    KEEP THE TRACKED VARIANT IN STEP WITH THE SELECTED ONE.

    Found 2026-07-28 in the browser: selecting a different variant updated the
    URL to ?variant=… and re-rendered the price, but the CTA kept the
    server-rendered data-sku and data-price of the FIRST available variant. A
    customer who picked "Lyserød / Med Håndliste" and clicked Køb produced an
    affiliate_click reporting the black, no-handrail SKU at the wrong price.

    That matters more here than it would elsewhere. The purchase completes
    off-site, so this event is the only conversion signal we own -- the whole
    reason it is instrumented before activation. An event carrying the wrong SKU
    and the wrong value does not merely under-report; it reports something false,
    and it would be reconciled against affiliate statements that disagree.

    The HREF is deliberately NOT updated. One affiliate URL per product is the
    documented design -- the supplier's page carries its own variant picker -- and
    that residual is recorded in the roadmap rather than fixed here.

    Guarded because pubsub.js is not loaded on every template that could contain
    a CTA. Without it the CTA still works and still tracks; it simply reports the
    default variant, which is the behaviour this replaces.
  */
  if (typeof subscribe === 'function' &&
      typeof PUB_SUB_EVENTS !== 'undefined' && PUB_SUB_EVENTS.variantChange) {
    subscribe(PUB_SUB_EVENTS.variantChange, function (event) {
      var variant = event && event.data && event.data.variant;
      if (!variant) return;
      var links = document.querySelectorAll('[data-affiliate-link]');
      for (var i = 0; i < links.length; i++) {
        if (typeof variant.sku === 'string') {
          links[i].setAttribute('data-sku', variant.sku);
        }
        if (typeof variant.price === 'number') {
          // Shopify prices are in minor units; data-price is decimal, matching
          // the Liquid `| divided_by: 100.0` that renders it.
          links[i].setAttribute('data-price', String(variant.price / 100));
        }
      }
    });
  }
})();
